import type { EChartsOption } from 'echarts';

type ChartPoint = { date: string; product: string; value: number | null };
// Stable product identities across views. Teal and mint lead; supporting hues distinguish series.
const palette = [
  '#0F464F',
  '#35B88B',
  '#447AB3',
  '#C18728',
  '#C96C5A',
  '#8B70AF',
  '#6D9443',
  '#428E98',
  '#B66D91',
  '#8B7964',
  '#677FB3',
  '#AD9651',
];
const knownProducts = [
  'Limited Time Lux Exclusive',
  'Limited Time Lux Partner',
  'Lux Partner Property',
  'Lux Partner Collection',
  'Flight',
  'Cruise',
  'Cruise Bank',
  'Tour',
  'Tour Bank',
  'Experience',
  'Hotel',
  'Insurance',
];
export const productColors = (name: string, dark = false) => {
  const known = knownProducts.indexOf(name);
  const hash = [...name].reduce((sum, ch) => (sum * 31 + ch.charCodeAt(0)) >>> 0, 0);
  const color = palette[(known < 0 ? hash : known) % palette.length];
  return dark && color === palette[0] ? '#4EFEB9' : color;
};
const money = (n: number) =>
  Math.abs(n) >= 1e6
    ? `$${(n / 1e6).toFixed(1)}m`
    : Math.abs(n) >= 1e3
      ? `$${(n / 1e3).toFixed(0)}k`
      : `$${n.toFixed(0)}`;
function seriesOption(
  points: Array<ChartPoint>,
  dark: boolean,
  mode: 'sales' | 'margin' | 'margin-value' | 'proportion' | 'weekly',
): EChartsOption {
  const dates = [...new Set(points.map((p) => p.date))].sort();
  const weight = new Map<string, number>();
  for (const point of points) {
    weight.set(point.product, (weight.get(point.product) ?? 0) + Math.abs(point.value ?? 0));
  }
  const names = [...weight.keys()].sort((a, b) => (weight.get(b) ?? 0) - (weight.get(a) ?? 0) || a.localeCompare(b));
  const lookup = new Map(points.map((p) => [`${p.product}\u0000${p.date}`, p.value]));
  const bars = mode === 'sales' || mode === 'proportion' || mode === 'margin-value';
  const percentage = mode === 'margin' || mode === 'proportion';
  const text = dark ? '#E8E4DA' : '#292B29';
  const grid = dark ? '#FFFFFF20' : '#0F0C081A';
  return {
    animation: false,
    backgroundColor: 'transparent',
    textStyle: { fontFamily: 'Inter, Arial, sans-serif', color: text },
    aria: {
      enabled: true,
      description: `${percentage ? 'Margin percentage' : mode === 'margin-value' ? 'Adjusted margin in AUD' : 'Sales in AUD'} by ${mode === 'weekly' ? 'Monday week' : 'Sydney transaction day'}. ${names.length} series: ${names.slice(0, 8).join(', ')}${names.length > 8 ? ', and more' : ''}. Missing values remain gaps. Use filters and the accompanying tables or CSV to inspect values.`,
      decal: { show: bars },
    },
    color: names.map((name) => productColors(name, dark)),
    grid: { left: 8, right: 24, top: 24, bottom: 76, containLabel: true },
    legend: {
      type: 'scroll',
      bottom: 0,
      left: 8,
      right: 8,
      textStyle: { color: text, fontSize: 11 },
      pageTextStyle: { color: text },
      pageIconColor: dark ? '#4EFEB9' : '#0F464F',
      itemWidth: 14,
      itemHeight: 9,
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      renderMode: 'richText',
      order: 'valueDesc',
      backgroundColor: dark ? '#25221E' : '#FFFFFF',
      borderColor: grid,
      textStyle: { color: text, fontSize: 12 },
      valueFormatter: (value) =>
        value == null
          ? 'No recorded value'
          : percentage
            ? `${(Number(value) * 100).toFixed(2)}%`
            : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(
                Number(value),
              ),
    },
    xAxis: {
      type: 'category',
      data: dates,
      boundaryGap: bars,
      axisLine: { lineStyle: { color: grid } },
      axisTick: { show: false },
      axisLabel: {
        color: text,
        hideOverlap: true,
        margin: 14,
        formatter: (value) =>
          new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
            new Date(`${value}T00:00:00Z`),
          ),
      },
    },
    yAxis: {
      type: 'value',
      ...(mode === 'proportion' &&
      points.every((point) => point.value === null || (point.value >= 0 && point.value <= 1))
        ? { min: 0, max: 1, interval: 0.2 }
        : {}),
      axisLabel: { color: text, formatter: (value) => (percentage ? `${(value * 100).toFixed(0)}%` : money(value)) },
      splitLine: { lineStyle: { color: grid } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: names.map((name, i) => ({
      name,
      type: bars ? 'bar' : 'line',
      ...(bars
        ? { stack: 'sales', barMaxWidth: 42 }
        : {
            smooth: false,
            connectNulls: false,
            showSymbol: dates.length < 15,
            symbolSize: 5,
            lineStyle: { width: 2.3, type: i >= palette.length ? 'dashed' : 'solid' },
          }),
      emphasis: { focus: 'series' },
      data: dates.map((date) => lookup.get(`${name}\u0000${date}`) ?? null),
    })),
  };
}
export const buildSalesOption = (points: Array<ChartPoint>, dark = false) => seriesOption(points, dark, 'sales');
export const buildMarginOption = (points: Array<ChartPoint>, dark = false) => seriesOption(points, dark, 'margin');
export const buildProportionOption = (points: Array<ChartPoint>, dark = false) =>
  seriesOption(points, dark, 'proportion');
export const buildWeeklyOption = (points: Array<ChartPoint>, dark = false) => seriesOption(points, dark, 'weekly');

export const buildMarginValueOption = (
  points: Array<ChartPoint & { metrics: { margin: number | null } }>,
  dark = false,
) =>
  seriesOption(
    points.map((point) => ({ ...point, value: point.metrics.margin })),
    dark,
    'margin-value',
  );
